/**
 * ClassTemplates API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for ClassTemplates with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  ClassTemplateCreate,
  ClassTemplateCreateInput,
  ClassTemplateFilters,
  ClassTemplateListResponse,
  ClassTemplateResponse,
  ClassTemplateUpdate,
  ClassTemplateUpdateInput,
} from "@/types/class_template"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of classTemplates with permission checks
 */
export function useClassTemplates(filters: ClassTemplateFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["classTemplates", filters],
    queryFn: async (): Promise<ClassTemplateListResponse> => {
      const response =
        await api.classTemplates.readClassTemplatesApiV1ClassTemplatesGet({
          limit: filters.limit,
          skip: filters.skip,
          search: filters.search,
          sortBy: filters.sort_by,
          sortOrder: filters.sort_order,
          createdBy: filters.created_by,
          createdAfter: filters.created_after,
          createdBefore: filters.created_before,
          updatedAfter: filters.updated_after,
          updatedBefore: filters.updated_before,
        })

      return unwrapStandardResponse<ClassTemplateListResponse>(
        response,
      ) as ClassTemplateListResponse
    },
    enabled: hasPermission("class_templates:read"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single classTemplate by ID with permission checks
 */
export function useClassTemplate(class_templateId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["classTemplates", class_templateId],
    queryFn: async (): Promise<ClassTemplateResponse> => {
      const response =
        await api.classTemplates.readClassTemplateApiV1ClassTemplatesClassTemplateIdGet(
          {
            classTemplateId: class_templateId,
          },
        )

      return unwrapStandardResponse<ClassTemplateResponse>(
        response,
      ) as ClassTemplateResponse
    },
    enabled: !!class_templateId && hasPermission("class_templates:read"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new classTemplate with validation and error handling
 */
export function useCreateClassTemplate() {
  return useApiMutation<
    ClassTemplateResponse,
    unknown,
    ClassTemplateCreateInput
  >({
    mutationFn: async (class_templateData) => {
      // Let the backend handle permission checks

      const class_templateCreate: ClassTemplateCreate = {
        name: class_templateData.name,
        slug: class_templateData.slug,
        description: class_templateData.description ?? null,
        program_type: class_templateData.program_type,
        instructor_id: class_templateData.instructor_id ?? null,
        swim_level: class_templateData.swim_level,
        location: class_templateData.location,
        day_of_week: class_templateData.day_of_week,
        start_time: class_templateData.start_time ?? null,
        end_time: class_templateData.end_time ?? null,
        duration_minutes: class_templateData.duration_minutes ?? null,
        max_students: class_templateData.max_students,
        min_students: class_templateData.min_students ?? null,
        current_enrollment: class_templateData.current_enrollment ?? null,
        waitlist_enabled: class_templateData.waitlist_enabled ?? null,
        price: class_templateData.price,
        prerequisites: class_templateData.prerequisites ?? null,
        notes: class_templateData.notes ?? null,
        is_active: class_templateData.is_active ?? null,
        is_published: class_templateData.is_published ?? null,
      }

      const response =
        await api.classTemplates.createClassTemplateApiV1ClassTemplatesPost({
          requestBody: class_templateCreate,
        })

      return unwrapStandardResponse<ClassTemplateResponse>(
        response,
      ) as ClassTemplateResponse
    },
    invalidateQueries: [["classTemplates"]],
    successToast: (data) => ({
      title: "ClassTemplate created successfully",
      description: ` "${data.id}" has been created.`,
    }),
  })
}

/**
 * Update existing classTemplate with validation and error handling
 */
export function useUpdateClassTemplate() {
  return useApiMutation<
    ClassTemplateResponse,
    unknown,
    { class_templateId: string; class_templateData: ClassTemplateUpdateInput }
  >({
    mutationFn: async ({
      class_templateId,
      class_templateData,
    }: {
      class_templateId: string
      class_templateData: ClassTemplateUpdateInput
    }) => {
      // Let the backend handle permission checks

      const class_templateUpdate: ClassTemplateUpdate = {
        name: class_templateData.name,
        slug: class_templateData.slug,
        description: class_templateData.description,
        program_type: class_templateData.program_type,
        instructor_id: class_templateData.instructor_id,
        swim_level: class_templateData.swim_level,
        location: class_templateData.location,
        day_of_week: class_templateData.day_of_week,
        start_time: class_templateData.start_time,
        end_time: class_templateData.end_time,
        duration_minutes: class_templateData.duration_minutes,
        max_students: class_templateData.max_students,
        min_students: class_templateData.min_students,
        current_enrollment: class_templateData.current_enrollment,
        waitlist_enabled: class_templateData.waitlist_enabled,
        price: class_templateData.price,
        prerequisites: class_templateData.prerequisites,
        notes: class_templateData.notes,
        is_active: class_templateData.is_active,
        is_published: class_templateData.is_published,
      }

      const response =
        await api.classTemplates.updateClassTemplateApiV1ClassTemplatesClassTemplateIdPut(
          {
            classTemplateId: class_templateId,
            requestBody: class_templateUpdate,
          },
        )

      return unwrapStandardResponse<ClassTemplateResponse>(
        response,
      ) as ClassTemplateResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["classTemplates", variables.class_templateId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["classTemplates"]],
  })
}

/**
 * Delete classTemplate with permission checks and error handling
 */
export function useDeleteClassTemplate() {
  return useApiMutation<{ class_templateId: string }, unknown, string>({
    mutationFn: async (class_templateId) => {
      // Let the backend handle permission checks
      await api.classTemplates.deleteClassTemplateApiV1ClassTemplatesClassTemplateIdDelete(
        { classTemplateId: class_templateId },
      )
      return { class_templateId }
    },
    removeQueries: [(variables) => ["classTemplates", variables]],
    invalidateQueries: [["classTemplates"]],
  })
}

/**
 * Invalidate classTemplates queries to force a refetch
 */
export function useInvalidateClassTemplates() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["classTemplates"] })
  }
}
